import { Component, Input, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgClass, NgStyle } from '@angular/common';
import { links } from 'src/app/core/links';
import { NavTabEnum } from 'src/app/core/enums/nav-tab.enum';
import { UserRoleEnum } from 'src/app/core/enums/user-role.enums';
import { UserContextService } from 'src/app/core/service/user-context.service';
import { ChartService } from 'src/app/core/service/chart.service';
import { WorkspaceService } from 'src/app/core/service/workspace.service';
import { environment } from 'src/environments/environment';
import { ArrayUtil } from 'src/app/core/utility/array.util';
import { FormattingUtil } from 'src/app/core/utility/formatting.util';
import { GlobalConstants } from 'src/app/core/global-constants';
import { BaseSelectorComponent } from '../../../shared/form/base-selector/base-selector.component';

@Component({
    selector: 'navigation-mobile-panel',
    templateUrl: './navigation-mobile-panel.component.html',
    styleUrls: ['./navigation-mobile-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, RouterLink, NgStyle, BaseSelectorComponent]
})

export class NavigationMobilePanelComponent {
    private context = inject(UserContextService);
    private chartService = inject(ChartService);
    private workspaceService = inject(WorkspaceService);
    private changeDetector = inject(ChangeDetectorRef);

    public reportOpen = false;
    public actionOpen = false;
    public adminSettingsOpen = false;
    public links = links;
    public role: UserRoleEnum;
    public documentLink = { name: 'Documents', href: links.documentsLink + '/' + links.documents.List };
    public actionLinks: ExtendedNavLink[] = [];
    public reportLinks: ExtendedNavLink[] = [];
    public adminSettings: ExtendedNavLink[] = null;
    public userName: string;
    public tabInFocus: NavTabEnum = 0;
    public enum = NavTabEnum;
    public currentPage: string;
    public isAdmin: boolean;
    public isCfo: boolean;
    public ncpRollover: string;
    public ncpCurrency: string;
    public showNcpRollover: boolean;
    public isBaseWorkspace: boolean;
    public averageSalesForLast3Months: string;
    public salesNeededToQualify: string;
    public adminUrl = links.adminSettingsLink + '/' + links.adminSettings.mainPage;
    public isNumber = (value: any) => !Number.isNaN(Number(value));

    public workspaces: { key: number, value: string }[] = [];
    public currentWorkspaceId: number = 1;
    // Stable single-element array for base-selector's checkedByDefault (compared by identity).
    public currentWorkspace: number[] = [];

    public async ngOnInit(): Promise<void> {
        this.isAdmin = this.context.isAdmin();
        this.isCfo = this.context.isCfo();
        this.isBaseWorkspace = this.context.getWorkspaceId() === GlobalConstants.baseWorkspaceId;
        this.showNcpRollover = (this.isAdmin || this.context.isRep()) && this.isBaseWorkspace;
        this.setLinks();
        await this.setWorkspaceSwitcher();
        await this.setLgfgSummaries();
        this.changeDetector.detectChanges();
    }

    private async setWorkspaceSwitcher(): Promise<void> {
        if (!this.context.canSwitchWorkspace()) {
            return;
        }
        // Inactive workspaces must not be selectable.
        const data = await this.workspaceService.getWorkspaces(true);
        this.workspaces = data.items.map(w => ({ key: w.id, value: w.name }));
        this.currentWorkspaceId = this.context.getWorkspaceId();
        this.currentWorkspace = [this.currentWorkspaceId];
    }

    public async onWorkspaceSelect(item: { key: number, value: string }): Promise<void> {
        if (!item || item.key === this.currentWorkspaceId) {
            return;
        }
        const jwt = await this.workspaceService.switchWorkspace(item.key);
        if (!jwt) return;
        sessionStorage.setItem(GlobalConstants.jwtSessionKey, jwt);
        document.cookie = GlobalConstants.jwtSessionKey + '=' + jwt;
        const user = this.context.user.value;
        user.workspaceId = item.key;
        user.workspaceName = item.value;
        this.context.setUser(user);
        window.location.reload();
    }

    // LGFG (base workspace) only figures.
    private async setLgfgSummaries(): Promise<void> {
        if (!this.isBaseWorkspace) {
            return;
        }
        const ncpRollover = await this.chartService.getNcpRollover();
        this.ncpRollover = FormattingUtil.getCommaSeparatedNumber(Math.round(ncpRollover.value));
        this.ncpCurrency = ncpRollover.currency;
        await this.setAverageSalesSummary();
    }

    private async setAverageSalesSummary(): Promise<void> {
        const summary = await this.chartService.getAverageSalesSummary();
        this.averageSalesForLast3Months = summary.averageAmount == 0 ? '0' : FormattingUtil.getCommaSeparatedNumber(Math.round(summary.averageAmount));
        this.salesNeededToQualify = FormattingUtil.getCommaSeparatedNumber(Math.round(summary.minAmount));
    }

    public onLinkClick(): void {
        document.getElementById('mainMenu').click();
    }

    public onReportClick(link: ExtendedNavLink): void {
        link.isSelected = !link.isSelected;
    }

    public onFolderClick(current: NavTabEnum): void {
        window.scrollTo(0, 0);
        if (this.tabInFocus === current) {
            this.tabInFocus = 0;
            return;
        }
        this.tabInFocus = current;
    }

    private setLinks(): void {
        const roles = this.context.getRoles();
        if (this.context.isAdmin()) {
            this.setAdminLinks();
            return;
        }
        if (this.context.isAccounting()) {
            this.setAccountingLinks();
            return;
        }
        roles.forEach(role => {
            if (role == UserRoleEnum.ea) {
                this.setEaLinks();
            }
            if (role == UserRoleEnum.sm) {
                this.setSmLinks();
            }
            if (role == UserRoleEnum.bb) {
                this.setBbLinks();
            }
            if (role == UserRoleEnum.rep) {
                this.setRepLinks();
            }
            if (role == UserRoleEnum.alterationApprover) {
                this.reportLinks.push({ name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport });
            }
            if (role == UserRoleEnum.expenseApprover) {
                this.reportLinks.push({ name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport });
            }
            if (role == UserRoleEnum.expenseSubmiter) {
                this.reportLinks.push({ name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport });
            }
        });

        if (this.actionLinks.length == 0) {
            this.actionLinks = null;
        }

        this.reportLinks = ArrayUtil.distinct(this.reportLinks, 'name');
        this.actionLinks = ArrayUtil.distinct(this.actionLinks, 'name');
    }

    private setAdminLinks(): void {
        this.reportLinks = [
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'Wholesale Sales Report', href: links.reportLink + '/' + links.reports.wholesaleMySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'Wholesale Items Report', href: links.reportLink + '/' + links.reports.wholesaleItemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Wholesale Cashflow Report', href: links.reportLink + '/' + links.reports.wholesaleCashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Management Report', href: links.reportLink + '/' + links.reports.managementReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'Top Clients Report', href: links.reportLink + '/' + links.reports.topClientReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport },
            { name: 'Top Service Report', href: links.reportLink + '/' + links.reports.topServiceReport },
            { name: 'Remake Item Report', href: links.reportLink + '/' + links.reports.remakeItemReport },
            { name: 'Profit Loss Report', href: links.reportLink + '/' + links.reports.profitLossReport },
            { name: 'Certificate Report', href: links.reportLink + '/' + links.reports.certificateReport },
            { name: 'Average Sales For Last 3 Month Report', href: links.reportLink + '/' + links.reports.averageSalesForLast3MonthReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Sales Chart', href: links.pagesLink + '/' + links.pages.salesChart },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ];

        this.actionLinks = [
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
            { name: 'My Yearly goal', href: links.pagesLink + '/' + links.pages.myGoal },
            //  { name: 'Achievements', href: links.achievements },
            { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer },
            { name: 'AI Chat', href: links.pagesLink + '/' + links.pages.aiChat },
        ];

        this.adminSettings = [
            { name: 'Item Management', href: '1' },
            { name: 'User Management', href: '2' },
            { name: 'Payments', href: '13' },
            { name: 'Survey Result', href: '3' },
            { name: 'Sales Management', href: '4' },
            { name: 'LGFG Certificates', href: '5' },
            { name: 'Payment Records', href: '21' },
            { name: 'Our Goal', href: '6' },
            { name: 'Exchange Rate to CAD', href: '9' },
            { name: 'Provinces and Cities', href: '10' },
            { name: 'Office management', href: '11' },
            { name: 'EA/Clothier Deposit Email Notifications', href: '7' },
            { name: 'Workspace Management', href: '22' },
            { name: 'Alteration Category Management', href: '12' },
            { name: 'Reassign Report', href: links.reportLink + '/' + links.reports.reassignReport },
            { name: 'Package Discount Management', href: '17' },
            { name: 'LGFG Companies Management Tab', href: '18' },
            { name: 'Growth awards YOY', href: '19' }
        ];

        if (this.context.isTechSupport()) {
            this.adminSettings.push({ name: 'Reassign Rule Management', href: '14' });
            this.adminSettings.push({ name: 'Logs', href: '8' });
            this.adminSettings.push({ name: 'Emails', href: '15' });
            this.adminSettings.push({ name: 'Remove Discount', href: '16' });
            this.adminSettings.push({ name: 'Hidden Users List', href: '20' });

            this.reportLinks.push({ name: 'QB Report', href: links.reportLink + '/' + links.reports.clothierQBReport });
            this.reportLinks.push({ name: 'New Clients Report', href: links.reportLink + '/' + links.reports.newClientReport });
            this.reportLinks.push({ name: 'Client Purchase Report', href: links.reportLink + '/' + links.reports.clientPurchaseReport });

            this.actionLinks.push({ name: 'Achievements', href: links.pagesLink + '/' + links.achievements });
        }
    }

    private setAccountingLinks(): void {
        this.reportLinks = [
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Management Report', href: links.reportLink + '/' + links.reports.managementReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport },
            { name: 'Remake Item Report', href: links.reportLink + '/' + links.reports.remakeItemReport },
            { name: 'Profit Loss Report', href: links.reportLink + '/' + links.reports.profitLossReport },
            { name: 'Certificate Report', href: links.reportLink + '/' + links.reports.certificateReport },
            { name: 'Average Sales For Last 3 Month Report', href: links.reportLink + '/' + links.reports.averageSalesForLast3MonthReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Sales Chart', href: links.pagesLink + '/' + links.pages.salesChart },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ];

        this.actionLinks = null;/*= [
            { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer }
        ];*/

        this.adminSettings = [
            { name: 'Item Management', href: '1' },
            { name: 'User Management', href: '2' },
            { name: 'Payments', href: '13' },
            { name: 'Sales Management', href: '4' },
            { name: 'LGFG Certificates', href: '5' },
            { name: 'Payment Records', href: '21' },
            { name: 'Exchange Rate to CAD', href: '9' },
            { name: 'Provinces and Cities', href: '10' },
            { name: 'Office management', href: '11' },
            { name: 'Alteration Category Management', href: '12' },
            { name: 'Package Discount Management', href: '17' }
        ];
        if (this.context.isCfo()) {
            this.adminSettings.push({ name: 'LGFG Companies Management Tab', href: '18' });
        }
    }

    private setEaLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
        ]);
    }

    private setSmLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'SM Report', href: links.reportLink + '/' + links.reports.socialMediaReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
        ]);
    }

    private setBbLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'BB Report', href: links.reportLink + '/' + links.reports.bookingBuddyReport }
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Appointment Card', href: links.appointmentCardLink + '/' + links.appointmentCard.List },
        ]);
    }

    private setRepLinks(): void {
        this.reportLinks = this.reportLinks.concat([
            { name: 'My Sales Report', href: links.reportLink + '/' + links.reports.mySalesReport },
            { name: 'My Items Report', href: links.reportLink + '/' + links.reports.itemsReport },
            { name: 'My Cashflow Report', href: links.reportLink + '/' + links.reports.cashflowReport },
            { name: 'Total Clothing and Alterations Budget Bank Report', href: links.reportLink + '/' + links.reports.totalNCPBankReport },
            { name: 'My Alterations Report', href: links.reportLink + '/' + links.reports.alterationsReport },
            { name: 'My Expenses Report', href: links.reportLink + '/' + links.reports.expensesReport },
            { name: 'My Payments Report', href: links.reportLink + '/' + links.reports.paymentReport },
            { name: 'Rep Overdue Report', href: links.reportLink + '/' + links.reports.overdueReport },
            { name: 'Rep Overview Report', href: links.reportLink + '/' + links.reports.overviewReport },
            { name: 'Cashflow Trailers Report', href: links.reportLink + '/' + links.reports.cashflowTrailorsReport },
            { name: 'Top Clients Report', href: links.reportLink + '/' + links.reports.topClientReport },
            { name: 'Bonus Report', href: links.reportLink + '/' + links.reports.bonusReport },
            { name: 'Referral Sales Report', href: links.reportLink + '/' + links.reports.referralSalesReport },
            { name: 'Quota Busters by Month', href: links.pagesLink + '/' + links.pages.qbByMonth },
        ]);

        this.actionLinks = this.actionLinks.concat([
            { name: 'DrivePipe', href: environment.pd },
            { name: 'Submit Payment', href: links.submitPaymentLink + '/' + links.submitPayment.List },
            { name: 'My Yearly goal', href: links.pagesLink + '/' + links.pages.myGoal },
            // { name: 'Achievements', href: links.achievements },
            //  { name: 'Clothier Career', href: links.pagesLink + '/' + links.pages.clothierCareer },
        ]);
    }
}
